import React from 'react';

/**
 * Shared controls + formatting for the expanded listing attributes.
 *
 * The important idea here is that a listing boolean has THREE states, not two:
 * Yes, No, and "not stated". A plain checkbox can only express two, so every listing
 * saved through one would silently record "No" for anything the agent left alone —
 * and a "No" counts against the property when it is scored, while "not stated" is
 * excluded from scoring entirely. Hence a tri-state control everywhere.
 *
 * All controls are full-width and wrap, so they hold up in a 360px portrait viewport.
 */

const FIELD_CLS =
  'w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white ' +
  'placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]';

/** Yes / No / Not stated. `value` is true, false or null/undefined. */
export function TriStateSelect({ label, value, onChange, hint, className = '' }) {
  const asString = value === true ? 'yes' : value === false ? 'no' : '';
  return (
    <div className={className}>
      {label && <label className="block text-sm text-gray-300 mb-1">{label}</label>}
      <select
        value={asString}
        onChange={(e) => {
          const v = e.target.value;
          onChange(v === 'yes' ? true : v === 'no' ? false : null);
        }}
        className={FIELD_CLS}
      >
        <option value="">Not stated</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
      {hint && <p className="text-gray-600 text-xs mt-1">{hint}</p>}
    </div>
  );
}

/** Read-only display for a tri-state listing boolean. */
export function TriStateValue({ value, yes = 'Yes', no = 'No' }) {
  if (value === null || value === undefined) {
    return <span className="text-gray-500" title="The listing did not state this">Not stated</span>;
  }
  return <span className={value ? 'text-[#00E676]' : 'text-gray-400'}>{value ? yes : no}</span>;
}

/** Comma-separated list rendered as compact chips; wraps cleanly at 360px. */
export function ListChips({ value, tone = 'cyan' }) {
  const items = String(value || '')
    .split(/[,;|]/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (!items.length) return <span className="text-gray-500">Not stated</span>;
  const cls =
    tone === 'green'
      ? 'bg-[#00E676]/10 text-[#00E676] border-[#00E676]/30'
      : 'bg-[#00D4FF]/10 text-[#00D4FF] border-[#00D4FF]/30';
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((it, i) => (
        <span key={i} className={`px-2 py-0.5 rounded text-[11px] font-medium border ${cls} max-w-full break-words`}>
          {it}
        </span>
      ))}
    </div>
  );
}

/**
 * A labelled row for the read-only detail panels. Renders nothing when the value is
 * absent AND `showEmpty` is false, so a sparse listing doesn't produce a wall of "—".
 */
export function DetailRow({ label, children, value, showEmpty = false }) {
  const empty = value === null || value === undefined || value === '';
  if (empty && !showEmpty && !children) return null;
  return (
    <div className="min-w-0">
      <span className="text-gray-500 text-xs">{label}</span>
      <div className="text-white text-sm break-words">
        {children || (empty ? <span className="text-gray-500">Not stated</span> : value)}
      </div>
    </div>
  );
}

/** The pet policy as one readable phrase from the two booleans (+ legacy free text). */
export function petsSummary(prop) {
  const { dogs_allowed: dogs, cats_allowed: cats, pets_allowed: legacy } = prop || {};
  if (dogs === null && cats === null && !legacy) return null;
  if (dogs === null && cats === null) return legacy;
  if (dogs && cats) return 'Dogs & cats OK';
  if (dogs) return 'Dogs OK';
  if (cats) return 'Cats OK';
  if (dogs === false && cats === false) return 'No pets';
  return legacy || null;
}

export { FIELD_CLS };
